import React, { Component, ErrorInfo, ReactNode } from 'react'
import { AlertCircle, RefreshCw } from '@digital-twins/design-system/icons'

/**
 * SlideErrorBoundary - Clean Olive & White Theme
 * 
 * Error boundary component for slide rendering.
 * Catches JavaScript errors in child components and displays a fallback UI.
 */

/**
 * The fallback's copy, in the viewer's language. It is the viewer's interface,
 * not slide content, so the host app supplies it from its own i18n.
 */
export interface SlideErrorBoundaryLabels {
  title: string
  message: string
  retry: string
  /** Summary of the development-only error details. */
  errorDetails: string
}

interface Props {
  children: ReactNode
  labels: SlideErrorBoundaryLabels
  /** Optional callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  /** Optional custom fallback component */
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class SlideErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })
    
    // Log to console for debugging
    console.error('[SlideErrorBoundary] Caught error:', error)
    console.error('[SlideErrorBoundary] Error info:', errorInfo)
    
    // Call optional error callback
    this.props.onError?.(error, errorInfo)
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI - Clean Olive & White Theme
      return (
        <div
          className="h-full flex flex-col items-center justify-center p-8 rounded-3xl"
          style={{ backgroundColor: 'var(--slide-highlight)' }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
          </div>
          
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            {this.props.labels.title}
          </h3>
          
          <p className="text-sm text-slate-500 mb-4 text-center max-w-md">
            {this.props.labels.message}
          </p>
          
          <button
            onClick={this.handleRetry}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-sm font-medium text-slate-700 transition-colors shadow-sm"
          >
            <RefreshCw className="w-4 h-4" />
            {this.props.labels.retry}
          </button>
          
          {/* Show error details in development */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="mt-6 w-full max-w-lg">
              <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">
                {this.props.labels.errorDetails}
              </summary>
              <pre className="mt-2 p-3 bg-slate-800 text-slate-100 rounded-xl text-xs overflow-auto max-h-48">
                {this.state.error.toString()}
                {'\n\n'}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Fallback component for when no data is available. The host supplies its
 * copy in the viewer's language.
 */
export function NoDataFallback({
  title,
  message,
  suggestion,
}: {
  title: string
  message: string
  suggestion?: string
}) {
  return (
    <div
      className="h-full flex flex-col items-center justify-center p-8 rounded-3xl"
      style={{ background: 'linear-gradient(135deg, var(--slide-highlight) 0%, var(--slide-card-bg) 100%)' }}
    >
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <AlertCircle className="w-8 h-8 text-slate-400" />
      </div>
      
      <h3 className="text-lg font-semibold text-slate-600 mb-2">
        {title}
      </h3>
      
      <p className="text-sm text-slate-400 mb-4 text-center max-w-md">
        {message}
      </p>
      
      {suggestion && (
        <p className="text-xs text-slate-400 text-center max-w-sm italic">
          {suggestion}
        </p>
      )}
    </div>
  )
}

export default SlideErrorBoundary
